/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { SiInstagram, SiFacebook, SiYoutube } from '@icons-pack/react-simple-icons';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { cn } from '@/lib/utils';
import { resolveSocialLinks, type SocialPlatform } from '@/utils/social-links';

/**
 * Display order of the flyout social row (Figma). Hrefs come from
 * `config.global.seo.organization.sameAs` so the row and the Organization JSON-LD stay in lockstep
 * (UCR-3004); a platform missing from that list is skipped rather than linked somewhere else.
 */
const SOCIAL = [
    {
        platform: 'instagram',
        Icon: SiInstagram,
        labelKey: 'socialMedia.instagramLabel',
        label: 'Instagram',
    },
    {
        platform: 'facebook',
        Icon: SiFacebook,
        labelKey: 'socialMedia.facebookLabel',
        label: 'Facebook',
    },
    {
        platform: 'youtube',
        Icon: SiYoutube,
        labelKey: 'socialMedia.youtubeLabel',
        label: 'YouTube',
    },
] as const satisfies ReadonlyArray<{ platform: SocialPlatform; [key: string]: unknown }>;

/** The Instagram / Facebook / YouTube row shared by the nav and search flyouts. */
export default function FlyoutSocial({ className }: { className?: string }): ReactElement | null {
    const { t } = useTranslation('footer');
    const links = resolveSocialLinks(useConfig());
    const items = SOCIAL.filter(({ platform }) => links[platform]);

    if (items.length === 0) return null;

    return (
        <div className={cn('flex items-center gap-6', className)}>
            {items.map(({ platform, Icon, labelKey, label }) => (
                <a
                    key={label}
                    href={links[platform]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t(labelKey, { defaultValue: label })}
                    className="transition-opacity hover:opacity-70">
                    <Icon className="size-[30px]" />
                </a>
            ))}
        </div>
    );
}
